'use client';

/**
 * useMediaQuery
 * Subscribes to a CSS media query and re-renders when it flips.
 *
 * Ported from glp-frontend `lib/hooks/useMediaQuery.ts`, rebuilt on
 * `useSyncExternalStore` so server rendering works: the server (and the
 * hydration pass) see `false`, and the real value takes over right after
 * hydration, with no hydration mismatch. A client-only app (glp-frontend)
 * reads the real value on the very first render, as glp's hook did.
 */

import { useCallback, useSyncExternalStore } from 'react';

/** Tailwind's `lg` breakpoint — the point where a side sheet stops crowding the page. */
export const LARGE_SCREEN_QUERY = '(min-width: 1024px)';

const supportsMatchMedia = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const getServerSnapshot = () => false;

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!supportsMatchMedia()) return () => undefined;
      const mediaQuery = window.matchMedia(query);
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = () => (supportsMatchMedia() ? window.matchMedia(query).matches : false);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export default useMediaQuery;
